//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';

import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import { getClientId } from '@redux/selectors/getClientId';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { useIsReRequestRequiredForMissingTCUrl } from '@uia/rights/hooks/useIsReRequestRequiredForMissingTCUrl';
import { getRightsRequestDetailsWithSyndicationCheck } from '@uia/rights/utils/rightsRequestUtils';
import { checkIsStatusInitiated } from '@uia/rights/utils/statusUtils';

interface Props {
  cuid: Cuid;
  rightsRequestText?: string;
}

export const ChooseMessageTCUrl = ({ cuid, rightsRequestText }: Props) => {
  const clientId = useSelector(getClientId);

  const {
    data: metadata,
    isError: isErrorMetadata,
    isPending: isPendingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    isError: isErrorRequestRequired,
    isLoading: isLoadingRequestRequired,
    isReRequestRequiredForMissingTCUrl,
  } = useIsReRequestRequiredForMissingTCUrl({
    communicationType: metadata?.rightsRequestStatus.communicationType,
    clientId,
    cuid,
    rightsRequestText,
    status: metadata?.rightsRequestStatus.status,
  });

  if (
    isPendingMetadata ||
    isErrorMetadata ||
    isLoadingRequestRequired ||
    isErrorRequestRequired
  ) {
    return null;
  }

  const { status } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  const areRightsAlreadyRequested = checkIsStatusInitiated(status);

  const brandPartnerText = metadata.cameFromSyndication
    ? 'by brand partner'
    : '';

  return isReRequestRequiredForMissingTCUrl || areRightsAlreadyRequested ? (
    <BodyText>Rights already requested {brandPartnerText} with:</BodyText>
  ) : (
    <BodyBoldText>Choose a message to use:</BodyBoldText>
  );
};
